/**
 * PassengerSatisfactionSystem.js
 *
 * Calculates passenger happiness based on flight delays, weather, diversions, etc.
 * Satisfaction affects reputation through PAX_SATISFACTION_80 event.
 */

// Satisfaction adjustment factors
export const SATISFACTION_FACTORS = {
  BASE_SATISFACTION: 85,           // Starting satisfaction %
  DELAY_PER_15MIN: -3,             // -3% per 15 minutes of delay
  WEATHER_DELAY_MITIGATION: 0.5,   // Weather delays count at 50% (passengers understand)
  DIVERSION_PENALTY: -15,          // Diversion hurts satisfaction significantly
  TURBULENCE_PENALTY: -5,          // Flying through bad weather = bumpy ride
  EARLY_ARRIVAL_BONUS: 5,          // Arriving early boosts satisfaction
  CANCELLED_PENALTY: -30,          // Cancelled flights hurt reputation via satisfaction
  CREW_SKILL_BONUS_PER_LEVEL: 2,   // +2% per crew skill level above 1
  LONG_DELAY_THRESHOLD: 90,        // Minutes - triggers extra penalty
  LONG_DELAY_EXTRA_PENALTY: -10,   // Extra penalty for delays over threshold
};

/**
 * Calculate passenger satisfaction for a completed flight
 *
 * @param {Object} flight - The completed flight object
 * @param {Array} delays - Array of delay objects from flight.delays
 * @param {Object} weatherAtOrigin - Weather impact at origin airport
 * @param {Object} weatherAtDest - Weather impact at destination airport
 * @param {number} crewSkillLevel - Average crew skill level (1-5)
 * @returns {Object} { satisfaction: 0-100, factors: [...breakdown], summary: string }
 */
export function calculateFlightSatisfaction(
  flight,
  delays = [],
  weatherAtOrigin = null,
  weatherAtDest = null,
  crewSkillLevel = 1
) {
  const factors = [];
  let satisfaction = SATISFACTION_FACTORS.BASE_SATISFACTION;

  factors.push({ name: 'Base', value: SATISFACTION_FACTORS.BASE_SATISFACTION, description: 'Starting satisfaction' });

  // Calculate total delay minutes
  const totalDelayMinutes = flight.totalDelayMinutes ||
    delays.reduce((sum, d) => sum + (d.minutes || 0), 0);

  // Separate weather delays from other delays
  const weatherDelayMinutes = delays
    .filter(d => d.type === 'weather' || d.type === 'enroute_weather')
    .reduce((sum, d) => sum + (d.minutes || 0), 0);

  const otherDelayMinutes = totalDelayMinutes - weatherDelayMinutes;

  // Apply delay penalties
  if (otherDelayMinutes > 0) {
    const delayPenalty = Math.floor(otherDelayMinutes / 15) * SATISFACTION_FACTORS.DELAY_PER_15MIN;
    satisfaction += delayPenalty;
    factors.push({
      name: 'Operational Delay',
      value: delayPenalty,
      description: `+${otherDelayMinutes}min delay`
    });
  }

  // Weather delays are more forgivable (passengers understand bad weather)
  if (weatherDelayMinutes > 0) {
    const weatherDelayPenalty = Math.floor(weatherDelayMinutes / 15) *
      SATISFACTION_FACTORS.DELAY_PER_15MIN * SATISFACTION_FACTORS.WEATHER_DELAY_MITIGATION;
    satisfaction += weatherDelayPenalty;
    factors.push({
      name: 'Weather Delay',
      value: Math.round(weatherDelayPenalty),
      description: `+${weatherDelayMinutes}min (weather, 50% impact)`
    });
  }

  // Extra penalty for very long delays
  if (totalDelayMinutes >= SATISFACTION_FACTORS.LONG_DELAY_THRESHOLD) {
    satisfaction += SATISFACTION_FACTORS.LONG_DELAY_EXTRA_PENALTY;
    factors.push({
      name: 'Extended Delay',
      value: SATISFACTION_FACTORS.LONG_DELAY_EXTRA_PENALTY,
      description: `Delay exceeded ${SATISFACTION_FACTORS.LONG_DELAY_THRESHOLD}min`
    });
  }

  // Diversion penalty
  if (flight.diverted || flight.divertedTo) {
    satisfaction += SATISFACTION_FACTORS.DIVERSION_PENALTY;
    factors.push({
      name: 'Diversion',
      value: SATISFACTION_FACTORS.DIVERSION_PENALTY,
      description: `Diverted to ${flight.divertedTo || 'alternate'}`
    });
  }

  // Turbulence/weather en-route penalty
  const hadEnRouteWeather = delays.some(d => d.type === 'enroute_weather') ||
    (weatherAtDest?.severity === 'delay' || weatherAtDest?.severity === 'grounded');

  if (hadEnRouteWeather && !flight.diverted) {
    satisfaction += SATISFACTION_FACTORS.TURBULENCE_PENALTY;
    factors.push({
      name: 'Turbulence',
      value: SATISFACTION_FACTORS.TURBULENCE_PENALTY,
      description: 'Weather during flight'
    });
  }

  // Early arrival bonus (negative delay)
  if (totalDelayMinutes < 0) {
    satisfaction += SATISFACTION_FACTORS.EARLY_ARRIVAL_BONUS;
    factors.push({
      name: 'Early Arrival',
      value: SATISFACTION_FACTORS.EARLY_ARRIVAL_BONUS,
      description: `Arrived ${Math.abs(totalDelayMinutes)}min early`
    });
  }

  // Crew skill bonus
  if (crewSkillLevel > 1) {
    const skillBonus = (crewSkillLevel - 1) * SATISFACTION_FACTORS.CREW_SKILL_BONUS_PER_LEVEL;
    satisfaction += skillBonus;
    factors.push({
      name: 'Crew Quality',
      value: skillBonus,
      description: `Skill level ${crewSkillLevel}`
    });
  }

  // Clamp to 0-100 range
  satisfaction = Math.max(0, Math.min(100, Math.round(satisfaction)));

  // Generate summary
  let summary;
  if (satisfaction >= 90) {
    summary = 'Excellent flight experience';
  } else if (satisfaction >= 80) {
    summary = 'Good flight experience';
  } else if (satisfaction >= 70) {
    summary = 'Acceptable experience';
  } else if (satisfaction >= 50) {
    summary = 'Below expectations';
  } else {
    summary = 'Poor experience';
  }

  return {
    satisfaction,
    factors,
    summary,
    meetsReputationThreshold: satisfaction >= 80, // For PAX_SATISFACTION_80 event
  };
}

/**
 * Calculate satisfaction for a cancelled flight (used for passenger rebooking impact)
 *
 * @param {Object} flight - The cancelled flight
 * @param {string} cancellationReason - Why it was cancelled
 * @returns {Object} Satisfaction result (always 0 for cancellation)
 */
export function calculateCancellationSatisfaction(flight, cancellationReason) {
  return {
    satisfaction: 0,
    factors: [
      { name: 'Cancellation', value: 0, description: cancellationReason || 'Flight cancelled' }
    ],
    summary: 'Flight cancelled',
    meetsReputationThreshold: false,
  };
}

/**
 * Get the average crew skill level for a flight
 *
 * @param {string} crewId - The crew assignment ID
 * @param {Array} crewList - List of all crew in worldState.crew
 * @returns {number} Average skill level (1-5)
 */
export function getCrewSkillLevel(crewId, crewList = []) {
  if (!crewId || !crewList || crewList.length === 0) return 1;

  // Find crew assigned to this flight
  const assignedCrew = crewList.filter(c => c.currentFlightId === crewId || c.assignment === crewId);

  if (assignedCrew.length === 0) return 1;

  // Average their skill levels
  const totalSkill = assignedCrew.reduce((sum, c) => sum + (c.skillLevel || c.skill || 1), 0);
  return Math.round(totalSkill / assignedCrew.length);
}

/**
 * Batch calculate satisfaction scores for daily reporting
 *
 * @param {Array} completedFlights - Array of completed flights for the day
 * @param {Object} airportWeather - Weather data by ICAO
 * @param {Array} crew - Crew list
 * @returns {Object} { averageSatisfaction, totalFlights, highSatisfactionCount, lowSatisfactionCount }
 */
export function calculateDailySatisfactionMetrics(completedFlights, airportWeather = {}, crew = []) {
  if (!completedFlights || completedFlights.length === 0) {
    return {
      averageSatisfaction: 0,
      totalFlights: 0,
      highSatisfactionCount: 0,
      lowSatisfactionCount: 0,
      belowThresholdCount: 0,
    };
  }

  let totalSatisfaction = 0;
  let highCount = 0; // >= 90
  let lowCount = 0;  // < 70
  let belowThreshold = 0; // < 80

  for (const flight of completedFlights) {
    const result = calculateFlightSatisfaction(
      flight,
      flight.delays || [],
      airportWeather[flight.origin]?.impact,
      airportWeather[flight.destination]?.impact,
      getCrewSkillLevel(flight.crewId, crew)
    );

    totalSatisfaction += result.satisfaction;

    if (result.satisfaction >= 90) highCount++;
    if (result.satisfaction < 70) lowCount++;
    if (result.satisfaction < 80) belowThreshold++;
  }

  return {
    averageSatisfaction: Math.round(totalSatisfaction / completedFlights.length),
    totalFlights: completedFlights.length,
    highSatisfactionCount: highCount,
    lowSatisfactionCount: lowCount,
    belowThresholdCount: belowThreshold,
  };
}

export default {
  SATISFACTION_FACTORS,
  calculateFlightSatisfaction,
  calculateCancellationSatisfaction,
  getCrewSkillLevel,
  calculateDailySatisfactionMetrics,
};
